import { WuiButton, WuiTopNav } from '@wui/react';
import capitalize from 'lodash-es/capitalize';
import { matchPath } from 'react-router-dom';

import { EXTERNAL_URLS } from '/config/constants';
import { destroySession } from '/global-state/session/actions';
import { useSession, useUserName } from '/global-state/session/hooks';
import { usePermissions } from '/module/permissions';
import { useCurrentRoute } from '/module/routing/hooks';
import { getAnchorProps } from '/module/routing/module';
import { ROUTES } from '/routes/config';

import { DEV_ONLY, USEFUL_LINKS, USER_MENU_SECTIONS } from './config';
import { useSystemStatusIndicator } from './system-status-indicator';
import { UpgradeBanner } from './upgrade-banner';
import { WHATS_NEW_CONTAINER_ID, WhatsNewButton } from './whats-new-button';
import { toKebabCase } from '/module/format';

export function TopNav() {
  const userName = useUserName();
  const currentRoute = useCurrentRoute();
  const showBackToPropertyButton = currentRoute?.uiOptions?.sideNav?.hidePropertySwitch;
  const permissions = usePermissions();

  const {
    companion: { fromAdmin },
    permissions: { role: userRole },
  } = useSession();
  const canUpgrade = permissions.accountHas('can_see_upgrade_page') && permissions.accountHas('self_service_upgrades');

  const statusIndicator = useSystemStatusIndicator();

  const userMenu = USER_MENU_SECTIONS.map((section) => {
    const title = section.displayName;
    const links = section.links
      .filter((item) => {
        // Remove routes that are not accessible to the user
        const route = item.link && ROUTES[item.link];
        const accessDenied =
          route && 'accessDenied' in route && route.accessDenied ? route.accessDenied(permissions) : false;
        return !accessDenied;
      })
      .map((linkItem) => {
        const route = linkItem.link && ROUTES[linkItem.link];
        const target = 'redirectExternal' in route && route.redirectExternal ? '_blank' : undefined;

        const highlight = !!matchPath(window.location.pathname, route);
        const anchorProps = getAnchorProps(linkItem.link, { target });
        return {
          name: linkItem.name,
          title: linkItem.name,
          'data-test': `link-${toKebabCase(linkItem.name)}`,
          highlight,
          ...anchorProps,
        };
      });

    if (links.length === 0) return null;

    return {
      title,
      links,
    };
  }).filter((section) => section !== null);

  const devOnly =
    import.meta.env.APP_JW_ENV === 'dev' || import.meta.env.APP_JW_ENV === 'stg'
      ? [
          {
            title: 'Dev only',
            links: [
              ...DEV_ONLY.map((linkItem) => {
                return {
                  name: linkItem.name,
                  title: linkItem.name,
                  'data-test': linkItem.dataTest,
                  onClick: () => linkItem.onClick(),
                };
              }),
            ],
          },
        ]
      : [];

  return (
    <section>
      <WuiTopNav
        userMenu={{
          displayName: `${userName || ''} (${capitalize(userRole)})`,
          warning: fromAdmin?.username ? `${fromAdmin?.username} as` : undefined,
          'data-test': 'open-settings-menu',
          sections: [
            ...userMenu,
            {
              title: 'Useful links',
              links: [
                ...USEFUL_LINKS.map((linkItem) => ({
                  name: linkItem.name,
                  href: linkItem.link,
                  'data-test': `link-${toKebabCase(linkItem.name)}`,
                  target: '_blank',
                })),
                ...(canUpgrade
                  ? [
                      {
                        name: 'Upgrade',
                        title: 'Upgrade',
                        href: EXTERNAL_URLS.salesContactForm,
                        'data-test': 'link-to-sales',
                        target: '_blank',
                      },
                    ]
                  : []),
              ],
            },
            ...devOnly,
          ],
          signoutButton: {
            onSignOut: destroySession,
            'data-test': 'toggle-sign-out',
          },
        }}
        statusIndicator={statusIndicator}
      >
        {showBackToPropertyButton && (
          <WuiButton
            data-test="back-to-property-dashboard"
            label={`Back to property`}
            as={['a', getAnchorProps('home')]}
            type="tertiary"
            icon="arrow-left"
            slot="left-side-content"
          />
        )}
        <div id={WHATS_NEW_CONTAINER_ID} slot="right-side-content" />;
      </WuiTopNav>
      <WhatsNewButton />
      <UpgradeBanner />
    </section>
  );
}
